import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CommissionCard } from "@/components/CommissionCard";
import { toast } from "sonner";
import {
  IdentificationCard,
  CreditCard,
  MapPin,
  UsersThree,
  Gear,
  NotePencil,
  PencilSimple,
  Check,
  X,
  Plus,
  Info,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { QboCustomerCard } from "@/components/QboCustomerCard";

const CURRENCIES = [
  ["USD", "US Dollar ($)"],
  ["CAD", "Canadian Dollar (C$)"],
  ["EUR", "Euro (€)"],
  ["GBP", "British Pound (£)"],
  ["AUD", "Australian Dollar (A$)"],
];
const PRICING_TIERS = ["Regular", "Contractor", "Designer", "Retail", "Builder"];
const PAY_TERMS = ["Due on receipt", "15 Days", "30 Days", "45 Days", "60 Days"];
const PAY_PLANS = [
  ["deposit", "50 / 50 — deposit slip + balance"],
  ["full", "Full invoice — one payment"],
];

const PORTAL_TOGGLES = [
  ["show_prices", "Quote pricing"],
  ["show_drawing", "Drawings"],
  ["show_layout", "Slab layout"],
  ["show_3d", "3D view"],
  ["show_job_progress", "Job progress"],
  ["show_photos", "Job photos"],
  ["show_documents", "Documents"],
  ["show_invoices", "Invoices & payments"],
  ["allow_online_payment", "Pay online"],
  ["allow_accept", "Accept quotes online"],
  ["allow_drawing_approval", "Drawing sign-off"],
];

const currencyLabel = (code) =>
  (CURRENCIES.find(([v]) => v === code) || [null, code || "USD"])[1];

const STATUS_OPTS = ["Lead", "Active", "Completed", "Inactive"];
const STATUS_CLS = {
  Lead: "border-sky-500/40 bg-sky-500/10 text-sky-300",
  Active: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
  Completed: "border-indigo-500/40 bg-indigo-500/10 text-indigo-300",
  Inactive: "border-zinc-600/50 bg-zinc-700/20 text-zinc-400",
};

const inputCls = "h-9 border-zinc-800 bg-zinc-950 text-sm";

const Section = ({ icon: Icon, title, onEdit, testid, action, children }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-900" data-testid={testid}>
    <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
      <span className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-zinc-300">
        <span className="flex h-6 w-6 items-center justify-center rounded-md bg-indigo-500/15 text-indigo-300">
          <Icon size={14} weight="bold" />
        </span>
        {title}
      </span>
      {onEdit ? (
        <button
          type="button"
          data-testid={`${testid}-edit`}
          onClick={onEdit}
          title="Edit"
          className="text-zinc-500 transition-colors duration-150 hover:text-indigo-300"
        >
          <PencilSimple size={15} weight="bold" />
        </button>
      ) : (
        action
      )}
    </div>
    <div className="px-4 py-3">{children}</div>
  </div>
);

const EditDialog = ({ open, title, onSave, onCancel, testid, children }) => (
  <Dialog
    open={open}
    onOpenChange={(o) => {
      if (!o) onCancel();
    }}
  >
    <DialogContent
      data-testid={`${testid}-dialog`}
      className="max-w-lg border-zinc-800 bg-zinc-900 text-zinc-100"
    >
      <DialogHeader>
        <DialogTitle className="text-sm font-bold uppercase tracking-widest text-zinc-200">
          {title}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Edit {title}
        </DialogDescription>
      </DialogHeader>
      <div className="max-h-[65vh] space-y-1 overflow-y-auto pr-1">
        {children}
      </div>
      <DialogFooter className="gap-2">
        <button
          type="button"
          data-testid={`${testid}-cancel`}
          onClick={onCancel}
          className="flex h-9 items-center justify-center rounded-md border border-zinc-700 px-4 text-[11px] font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid={`${testid}-save`}
          onClick={onSave}
          className="flex h-9 items-center gap-1 rounded-md bg-indigo-500 px-4 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
        >
          <Check size={13} weight="bold" /> Save
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

const Row = ({ label, help, children }) => (
  <div className="grid grid-cols-[130px_1fr] items-start gap-3 py-[7px]">
    <span className="flex items-center gap-1 text-sm text-zinc-500">
      {label}
      {help && <Info size={13} weight="bold" className="text-zinc-600" />}
    </span>
    <span className="min-w-0 break-words text-sm text-zinc-200">
      {children === "" || children === undefined || children === null ? (
        <span className="text-zinc-600">—</span>
      ) : (
        children
      )}
    </span>
  </div>
);

const EditRow = ({ label, children }) => (
  <div className="grid grid-cols-[130px_1fr] items-center gap-3 py-1">
    <span className="text-sm text-zinc-500">{label}</span>
    <div className="min-w-0">{children}</div>
  </div>
);

export const CustomerProfile = ({ c, id, onSaved }) => {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [crew, setCrew] = useState([]);

  useEffect(() => {
    api
      .get("/crew-list")
      .then(({ data }) => setCrew(data || []))
      .catch(() => setCrew([]));
  }, []);

  const salesOpts = crew.map((u) => u.name).filter(Boolean);
  const groupOpts = [...new Set(crew.map((u) => u.role).filter(Boolean))];

  const begin = (card, initial) => {
    setEditing(card);
    setDraft(initial);
  };
  const cancel = () => {
    setEditing(null);
    setDraft({});
  };
  const save = async () => {
    try {
      const { data } = await api.patch(`/customers/${id}`, draft);
      onSaved(data);
      setEditing(null);
      setDraft({});
      toast.success("Account updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save",
      );
    }
  };
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setNested = (obj, k, v) =>
    setDraft((d) => ({ ...d, [obj]: { ...(d[obj] || {}), [k]: v } }));

  const team = c.internal_team || {};
  const adv = c.advanced_config || {};
  const isOrg = (c.contact_type || (c.company ? "organization" : "person")) === "organization";
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (!isOrg) return;
    api.get(`/customers/${id}/people`).then(({ data }) => setPeople(data || [])).catch(() => setPeople([]));
  }, [id, isOrg]);

  const cardProps = (key, icon, title, initial) => ({
    icon,
    title,
    testid: `profile-${key}`,
    onEdit: () => begin(key, initial),
  });
  const dialogProps = (key, title) => ({
    open: editing === key,
    title,
    onSave: save,
    onCancel: cancel,
    testid: `profile-${key}`,
  });

  return (
    <div className="space-y-4" data-testid="customer-profile">
      <div className="grid items-start gap-4 lg:grid-cols-3">
        {/* CONTACT DETAILS */}
        <Section
          {...cardProps("contact", IdentificationCard, "Contact Details", {
            name: c.name || "",
            company: c.company || c.parent_org || "",
            status: c.status || "Lead",
            phone: c.phone || "",
            secondary_phone: c.secondary_phone || "",
            email: c.email || "",
            fax: c.fax || "",
          })}
        >
          <Row label="Name">{c.name}</Row>
          <Row label="Company Name">{c.company || c.parent_org}</Row>
          <Row label="Status">
            <span
              data-testid="profile-status-badge"
              className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                STATUS_CLS[c.status] || STATUS_CLS.Lead
              }`}
            >
              {c.status || "Lead"}
            </span>
          </Row>
          <Row label="Primary Phone">{c.phone}</Row>
          <Row label="Secondary Phone">{c.secondary_phone}</Row>
          <Row label="E-mail">{c.email}</Row>
          <Row label="Fax">{c.fax}</Row>
        </Section>
        <EditDialog {...dialogProps("contact", "Contact Details")}>
          <EditRow label="Name">
            <Input data-testid="edit-name" className={inputCls}
              value={draft.name ?? ""} onChange={(e) => set("name", e.target.value)} />
          </EditRow>
          <EditRow label="Company Name">
            <Input data-testid="edit-company" className={inputCls}
              value={draft.company ?? ""} onChange={(e) => set("company", e.target.value)} />
          </EditRow>
          <EditRow label="Status">
            <Select value={draft.status || "Lead"} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="edit-status" className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditRow>
          <EditRow label="Primary Phone">
            <Input data-testid="edit-phone" className={inputCls}
              value={draft.phone ?? ""} onChange={(e) => set("phone", e.target.value)} />
          </EditRow>
          <EditRow label="Secondary Phone">
            <Input data-testid="edit-secondary-phone" className={inputCls}
              value={draft.secondary_phone ?? ""} onChange={(e) => set("secondary_phone", e.target.value)} />
          </EditRow>
          <EditRow label="E-mail">
            <Input data-testid="edit-email" className={inputCls}
              value={draft.email ?? ""} onChange={(e) => set("email", e.target.value)} />
          </EditRow>
          <EditRow label="Fax">
            <Input data-testid="edit-fax" className={inputCls}
              value={draft.fax ?? ""} onChange={(e) => set("fax", e.target.value)} />
          </EditRow>
        </EditDialog>

        {isOrg && (
          <Section
            icon={UsersThree}
            title={`People (${people.length})`}
            testid="profile-people"
            action={
              <Link
                to={`/customers/new?organization_id=${id}`}
                data-testid="profile-people-add"
                className="text-[11px] font-semibold text-indigo-300 hover:text-indigo-200"
              >
                + Add person
              </Link>
            }
          >
            {people.length === 0 ? (
              <div className="py-2 text-sm text-zinc-600">No people linked to this organization yet.</div>
            ) : (
              <div className="divide-y divide-zinc-800/70">
                {people.map((p) => (
                  <Link
                    key={p.id}
                    to={`/customers/${p.id}`}
                    data-testid={`profile-person-${p.id}`}
                    className="flex items-center justify-between gap-3 py-2 text-sm transition-colors hover:text-indigo-200"
                  >
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-zinc-200">{p.name}</span>
                      <span className="block truncate text-xs text-zinc-500">
                        {[p.phone, p.email].filter(Boolean).join(" · ")}
                      </span>
                    </span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wider text-zinc-500">{p.status}</span>
                  </Link>
                ))}
              </div>
            )}
          </Section>
        )}

        {isOrg && <CommissionCard id={id} onCredits={(v) => onSaved({ ...c, credits: v })} />}

        {/* PAYMENT INFORMATION */}
        <Section
          {...cardProps("payment", CreditCard, "Payment Information", {
            currency: c.currency || "USD",
            pricing_tier: c.pricing_tier || c.group || "Regular",
            sales_tax: c.sales_tax || "",
            payment_terms: c.payment_terms || "",
            pay_plan: c.pay_plan || "deposit",
            accounting_email: c.accounting_email || "",
            accounting_phone: c.accounting_phone || "",
          })}
        >
          <Row label="Currency" help>{currencyLabel(c.currency)}</Row>
          <Row label="Pricing Tier">{c.pricing_tier || c.group}</Row>
          <Row label="Sales Tax">{c.sales_tax}</Row>
          <Row label="Payment Terms">{c.payment_terms}</Row>
          <Row label="How they pay">
            <span data-testid="view-pay-plan">
              {(PAY_PLANS.find(([v]) => v === (c.pay_plan || "deposit")) || PAY_PLANS[0])[1]}
            </span>
          </Row>
          <Row label="Accounting E-mail/s">{c.accounting_email}</Row>
          <Row label="Accounting Phone">{c.accounting_phone}</Row>
        </Section>
        <EditDialog {...dialogProps("payment", "Payment Information")}>
          <EditRow label="Currency">
            <Select value={draft.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="edit-currency" className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditRow>
          <EditRow label="Pricing Tier">
            <Select value={draft.pricing_tier} onValueChange={(v) => set("pricing_tier", v)}>
              <SelectTrigger data-testid="edit-pricing-tier" className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRICING_TIERS.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditRow>
          <EditRow label="Sales Tax">
            <Input data-testid="edit-sales-tax" className={inputCls}
              value={draft.sales_tax ?? ""} placeholder="0% No Tax"
              onChange={(e) => set("sales_tax", e.target.value)} />
          </EditRow>
          <EditRow label="Payment Terms">
            <Select value={draft.payment_terms || undefined}
              onValueChange={(v) => set("payment_terms", v)}>
              <SelectTrigger data-testid="edit-payment-terms" className={inputCls}>
                <SelectValue placeholder="Select terms" />
              </SelectTrigger>
              <SelectContent>
                {PAY_TERMS.map((v) => (
                  <SelectItem key={v} value={v}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditRow>
          <EditRow label="How they pay">
            <Select value={draft.pay_plan || "deposit"}
              onValueChange={(v) => set("pay_plan", v)}>
              <SelectTrigger data-testid="edit-pay-plan" className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAY_PLANS.map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </EditRow>
          <EditRow label="Accounting E-mail/s">
            <Input data-testid="edit-accounting-email" className={inputCls}
              value={draft.accounting_email ?? ""}
              onChange={(e) => set("accounting_email", e.target.value)} />
          </EditRow>
          <EditRow label="Accounting Phone">
            <Input data-testid="edit-accounting-phone" className={inputCls}
              value={draft.accounting_phone ?? ""}
              onChange={(e) => set("accounting_phone", e.target.value)} />
          </EditRow>
        </EditDialog>

        {/* ADDRESSES */}
        <Section
          {...cardProps("addresses", MapPin, "Addresses", {
            main_address: c.main_address || c.address || "",
            billing_address: c.billing_address || "",
            job_addresses: [...(c.job_addresses || [])],
          })}
        >
          <Row label="Main Address">{c.main_address || c.address}</Row>
          <Row label="Billing Address">{c.billing_address}</Row>
          <Row label="Job Addresses">
            {(c.job_addresses || []).length > 0 ? (
              <span className="space-y-1">
                {c.job_addresses.map((a, i) => (
                  <a key={i} href={`https://maps.google.com/?q=${encodeURIComponent(a)}`}
                    target="_blank" rel="noreferrer"
                    data-testid={`profile-job-address-${i}`}
                    className="block hover:text-indigo-300">{a}</a>
                ))}
              </span>
            ) : (
              ""
            )}
          </Row>
        </Section>
        <EditDialog {...dialogProps("addresses", "Addresses")}>
          <EditRow label="Main Address">
            <Input data-testid="edit-main-address" className={inputCls}
              value={draft.main_address ?? ""}
              onChange={(e) => set("main_address", e.target.value)} />
          </EditRow>
          <EditRow label="Billing Address">
            <Input data-testid="edit-billing-address" className={inputCls}
              value={draft.billing_address ?? ""}
              onChange={(e) => set("billing_address", e.target.value)} />
          </EditRow>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-sm text-zinc-500">Job Addresses</span>
            <button type="button" data-testid="add-job-address"
              onClick={() => set("job_addresses", [...(draft.job_addresses || []), ""])}
              className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-indigo-300">
              <Plus size={12} weight="bold" /> Add
            </button>
          </div>
          {(draft.job_addresses || []).map((a, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input data-testid={`edit-job-address-${i}`} className={inputCls} value={a}
                onChange={(e) => set("job_addresses",
                  draft.job_addresses.map((x, j) => (j === i ? e.target.value : x)))} />
              <button type="button" data-testid={`remove-job-address-${i}`}
                onClick={() => set("job_addresses",
                  draft.job_addresses.filter((_, j) => j !== i))}
                className="shrink-0 px-2 text-zinc-500 hover:text-red-400">
                <X size={14} weight="bold" />
              </button>
            </div>
          ))}
        </EditDialog>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-3">
        {/* INTERNAL TEAM */}
        <Section
          {...cardProps("team", UsersThree, "Internal Team", {
            internal_team: { ...team },
          })}
        >
          <Row label="Primary Sales">{team.primary_sales}</Row>
          <Row label="Secondary Sales">{team.secondary_sales}</Row>
          <Row label="Managing Group">{team.managing_group}</Row>
        </Section>
        <EditDialog {...dialogProps("team", "Internal Team")}>
          {[
            ["primary_sales", "Primary Sales", salesOpts],
            ["secondary_sales", "Secondary Sales", salesOpts],
            ["managing_group", "Managing Group", groupOpts],
          ].map(([k, label, opts]) => {
            const cur = draft.internal_team?.[k] || "";
            return (
              <EditRow key={k} label={label}>
                <Select
                  value={cur || "__none__"}
                  onValueChange={(v) =>
                    setNested("internal_team", k, v === "__none__" ? "" : v)
                  }
                >
                  <SelectTrigger data-testid={`edit-team-${k}`} className={inputCls}>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {cur && !opts.includes(cur) && (
                      <SelectItem value={cur}>{cur}</SelectItem>
                    )}
                    {opts.map((o) => (
                      <SelectItem key={o} value={o}>
                        {k === "managing_group"
                          ? o.charAt(0).toUpperCase() + o.slice(1)
                          : o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </EditRow>
            );
          })}
        </EditDialog>

        {/* ADVANCED CONFIGURATION */}
        <Section
          {...cardProps("advanced", Gear, "Advanced Configuration", {
            advanced_config: { ...adv },
            portal_visibility: { ...(c.portal_visibility || {}) },
          })}
        >
          <Row label="Task Restrictions">{adv.task_restrictions || "No restrictions"}</Row>
          <Row label="Auto Apply Discount" help>{adv.auto_apply_discount || "None"}</Row>
          <Row label="Payment Links">{adv.payment_links || "Not specified"}</Row>
          <Row label="Portal Access">
            {(() => {
              const hidden = PORTAL_TOGGLES.filter(
                ([k]) => (c.portal_visibility || {})[k] === false,
              ).map(([, l]) => l);
              return hidden.length === 0 ? (
                "All visible"
              ) : (
                <span className="text-amber-300">
                  Hidden: {hidden.join(", ")}
                </span>
              );
            })()}
          </Row>
        </Section>
        <QboCustomerCard customerId={id} />
        <EditDialog {...dialogProps("advanced", "Advanced Configuration")}>
          <EditRow label="Task Restrictions">
            <Input data-testid="edit-adv-task" className={inputCls}
              value={draft.advanced_config?.task_restrictions || ""}
              placeholder="No restrictions"
              onChange={(e) => setNested("advanced_config", "task_restrictions", e.target.value)} />
          </EditRow>
          <EditRow label="Auto Apply Discount">
            <Input data-testid="edit-adv-discount" className={inputCls}
              value={draft.advanced_config?.auto_apply_discount || ""}
              placeholder="None"
              onChange={(e) => setNested("advanced_config", "auto_apply_discount", e.target.value)} />
          </EditRow>
          <EditRow label="Payment Links">
            <Input data-testid="edit-adv-payment-links" className={inputCls}
              value={draft.advanced_config?.payment_links || ""}
              placeholder="Not specified"
              onChange={(e) => setNested("advanced_config", "payment_links", e.target.value)} />
          </EditRow>
          <div className="pt-2">
            <div className="mb-1.5 text-sm text-zinc-500">Portal Access</div>
            <div className="flex flex-wrap gap-1.5" data-testid="portal-access-toggles">
              {PORTAL_TOGGLES.map(([k, label]) => {
                const on = draft.portal_visibility?.[k] !== false;
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`portal-toggle-${k}`}
                    onClick={() => setNested("portal_visibility", k, !on)}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors duration-150 ${
                      on
                        ? "border-indigo-500/40 bg-indigo-500/10 text-indigo-200"
                        : "border-zinc-700 bg-zinc-950 text-zinc-600 line-through"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[10px] text-zinc-600">
              Tap to hide. Global portal settings still apply — this only
              hides more from this account.
            </p>
          </div>
        </EditDialog>
      </div>

      {/* NOTES */}
      <Section {...cardProps("notes", NotePencil, "Notes", { notes: c.notes || "" })}>
        <p className="whitespace-pre-wrap text-sm text-zinc-300" data-testid="profile-notes-text">
          {c.notes || <span className="text-zinc-600">No notes yet.</span>}
        </p>
      </Section>
      <EditDialog {...dialogProps("notes", "Notes")}>
        <textarea
          data-testid="edit-notes"
          rows={5}
          value={draft.notes ?? ""}
          onChange={(e) => set("notes", e.target.value)}
          placeholder="Internal notes about this account…"
          className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-950 p-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:border-indigo-500 focus:outline-none"
        />
      </EditDialog>
    </div>
  );
};
